import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Badge,
  Spinner,
  useToast,
  Button,
  HStack,
  useDisclosure,
  Input,
  Select,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { AddIcon, ChevronLeftIcon, ChevronRightIcon, DeleteIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import WhatsappOptOutsApi from '../../../../api/whatsappOptOuts';
import WhatsappOptOutFormModal from './WhatsappOptOutFormModal';
import { prettifyDate } from '../../../../functions/dateUtils';

const PAGE_SIZE = 20;

const SOURCE_BADGE = {
  reply: { label: 'תגובה', colorScheme: 'orange' },
  admin: { label: 'ידני', colorScheme: 'purple' },
  backfill: { label: 'ייבוא', colorScheme: 'gray' },
};

const SourceBadge = ({ source }) => {
  const info = SOURCE_BADGE[source] || { label: source, colorScheme: 'gray' };
  return <Badge colorScheme={info.colorScheme}>{info.label}</Badge>;
};

const WhatsappOptOutsModal = ({ isOpen, onClose }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('ALL');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const searchTimer = useRef(null);

  const [pendingRemove, setPendingRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const cancelRef = useRef();

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const toast = useToast();

  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();

  const fetchData = async (overrides = {}) => {
    const effective = {
      search,
      source,
      fromDate,
      toDate,
      pageIndex,
      ...overrides,
    };
    setLoading(true);
    try {
      const res = await WhatsappOptOutsApi.getAll({
        search: effective.search || undefined,
        source: effective.source,
        fromDate: effective.fromDate || undefined,
        toDate: effective.toDate || undefined,
        limit: PAGE_SIZE,
        offset: effective.pageIndex * PAGE_SIZE,
      });
      const payload = res.data || {};
      setItems(payload.items || []);
      setTotal(payload.total || 0);
    } catch (err) {
      toast({ title: 'שגיאה בטעינת נתונים', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setSource('ALL');
      setFromDate('');
      setToDate('');
      setPageIndex(0);
      fetchData({ search: '', source: 'ALL', fromDate: '', toDate: '', pageIndex: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setPageIndex(0);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchData({ search: value, pageIndex: 0 });
    }, 300);
  };

  const handleSourceChange = (e) => {
    const value = e.target.value;
    setSource(value);
    setPageIndex(0);
    fetchData({ source: value, pageIndex: 0 });
  };

  const handleFromDateChange = (e) => {
    const value = e.target.value;
    setFromDate(value);
    setPageIndex(0);
    fetchData({ fromDate: value, pageIndex: 0 });
  };

  const handleToDateChange = (e) => {
    const value = e.target.value;
    setToDate(value);
    setPageIndex(0);
    fetchData({ toDate: value, pageIndex: 0 });
  };

  const goToPage = (next) => {
    setPageIndex(next);
    fetchData({ pageIndex: next });
  };

  const confirmRemove = (row) => setPendingRemove(row);
  const cancelRemove = () => setPendingRemove(null);
  const doRemove = async () => {
    if (!pendingRemove) return;
    setRemoving(true);
    try {
      await WhatsappOptOutsApi.remove({ phoneNumber: pendingRemove.phoneNumber });
      toast({ title: 'הוסר בהצלחה', status: 'success', duration: 3000, isClosable: true });
      setPendingRemove(null);
      fetchData();
    } catch (err) {
      const msg = err?.response?.data?.message || 'שגיאה בהסרה';
      toast({ title: msg, status: 'error', duration: 3000, isClosable: true });
    } finally {
      setRemoving(false);
    }
  };

  const handleExport = () => {
    const rows = items.map((row) => ({
      'טלפון': row.phoneNumber || '',
      'שם': row.user ? `${row.user.firstName || ''} ${row.user.lastName || ''}`.trim() : '',
      'חברה': row.user?.companyName || '',
      'מקור': SOURCE_BADGE[row.source]?.label || row.source || '',
      'הודעה / סיבה': row.rawMessage || '',
      'נוסף על ידי': row.addedBy
        ? `${row.addedBy.firstName || ''} ${row.addedBy.lastName || ''}`.trim()
        : '',
      'תאריך הסרה': row.createdAt ? prettifyDate(row.createdAt) : '',
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'opt-outs');
    XLSX.writeFile(workbook, 'whatsapp_opt_outs.xlsx');
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="6xl">
        <ModalOverlay />
        <ModalContent maxW="90%" h="85vh" display="flex" flexDirection="column">
          <ModalCloseButton position="absolute" left="8px" right="auto" top="8px" zIndex="3" />
          <ModalHeader flexShrink={0}>
            <Flex align="center" justify="space-between" flexWrap="wrap" gap={3}>
              <HStack spacing={3}>
                <Text>רשימת הסרה - WhatsApp</Text>
                <Badge colorScheme="orange" fontSize="md" px={2}>
                  {total}
                </Badge>
              </HStack>
              <HStack spacing={2}>
                <Button leftIcon={<AddIcon />} colorScheme="orange" size="sm" onClick={onFormOpen}>
                  הוסף מספר
                </Button>
                <Button size="sm" variant="outline" onClick={handleExport} isDisabled={items.length === 0}>
                  ייצוא לאקסל
                </Button>
              </HStack>
            </Flex>
          </ModalHeader>
          <ModalBody pb={2} display="flex" flexDirection="column" overflow="hidden">
            <HStack mb={4} spacing={3} flexShrink={0} flexWrap="wrap">
              <Input
                placeholder="חיפוש לפי מספר טלפון"
                value={search}
                onChange={handleSearchChange}
                maxW="260px"
              />
              <Select value={source} onChange={handleSourceChange} maxW="180px">
                <option value="ALL">כל המקורות</option>
                <option value="reply">תגובה (הסר)</option>
                <option value="admin">ידני</option>
                <option value="backfill">ייבוא</option>
              </Select>
              <HStack spacing={1}>
                <Text fontSize="sm" color="gray.500">מ-</Text>
                <Input type="date" value={fromDate} onChange={handleFromDateChange} maxW="160px" />
              </HStack>
              <HStack spacing={1}>
                <Text fontSize="sm" color="gray.500">עד-</Text>
                <Input type="date" value={toDate} onChange={handleToDateChange} maxW="160px" />
              </HStack>
            </HStack>

            <Box flex="1" overflowY="auto" overflowX="auto">
              {loading ? (
                <Flex justify="center" align="center" h="100%">
                  <Spinner size="xl" />
                </Flex>
              ) : items.length === 0 ? (
                <Flex justify="center" align="center" h="100%">
                  <Text color="gray.500">אין תוצאות</Text>
                </Flex>
              ) : (
                <Table variant="simple" color="gray.500">
                  <Thead position="sticky" top={0} zIndex={1} bg="white">
                    <Tr>
                      <Th borderColor={borderColor}>טלפון</Th>
                      <Th borderColor={borderColor}>שם</Th>
                      <Th borderColor={borderColor}>חברה</Th>
                      <Th borderColor={borderColor}>מקור</Th>
                      <Th borderColor={borderColor}>הודעה / סיבה</Th>
                      <Th borderColor={borderColor}>נוסף על ידי</Th>
                      <Th borderColor={borderColor}>תאריך</Th>
                      <Th borderColor={borderColor}>פעולות</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {items.map((row) => {
                      const name = row.user
                        ? `${row.user.firstName || ''} ${row.user.lastName || ''}`.trim()
                        : '';
                      const addedByName = row.addedBy
                        ? `${row.addedBy.firstName || ''} ${row.addedBy.lastName || ''}`.trim()
                        : '';
                      return (
                        <Tr key={row.id}>
                          <Td fontSize="sm" color={textColor} borderColor={borderColor}>
                            {row.phoneNumber}
                          </Td>
                          <Td fontSize="sm" color={textColor} borderColor={borderColor}>
                            {name || <Text as="span" color="gray.400">לא רשום</Text>}
                          </Td>
                          <Td fontSize="sm" color={textColor} borderColor={borderColor}>
                            {row.user?.companyName || <Text as="span" color="gray.400">—</Text>}
                          </Td>
                          <Td fontSize="sm" borderColor={borderColor}>
                            <SourceBadge source={row.source} />
                          </Td>
                          <Td fontSize="sm" color={textColor} borderColor={borderColor} maxW="280px">
                            <Text noOfLines={2} title={row.rawMessage || ''}>
                              {row.rawMessage || '—'}
                            </Text>
                          </Td>
                          <Td fontSize="sm" color={textColor} borderColor={borderColor}>
                            {addedByName || <Text as="span" color="gray.400">—</Text>}
                          </Td>
                          <Td fontSize="sm" color={textColor} borderColor={borderColor}>
                            {row.createdAt ? prettifyDate(row.createdAt) : '—'}
                          </Td>
                          <Td borderColor={borderColor}>
                            <IconButton
                              icon={<DeleteIcon />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              aria-label="הסר מרשימת הסרה"
                              onClick={() => confirmRemove(row)}
                            />
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              )}
            </Box>

            {pageCount > 1 && (
              <Flex justify="center" align="center" pt={3} gap={3} flexShrink={0}>
                <IconButton
                  icon={<ChevronRightIcon />}
                  size="sm"
                  onClick={() => goToPage(pageIndex - 1)}
                  isDisabled={pageIndex === 0 || loading}
                  aria-label="previous"
                />
                <Text fontSize="sm" color="gray.500">
                  עמוד {pageIndex + 1} מתוך {pageCount}
                </Text>
                <IconButton
                  icon={<ChevronLeftIcon />}
                  size="sm"
                  onClick={() => goToPage(pageIndex + 1)}
                  isDisabled={pageIndex >= pageCount - 1 || loading}
                  aria-label="next"
                />
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <WhatsappOptOutFormModal
        isOpen={isFormOpen}
        onClose={onFormClose}
        onSuccess={() => fetchData()}
      />

      <AlertDialog
        isOpen={!!pendingRemove}
        leastDestructiveRef={cancelRef}
        onClose={cancelRemove}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              הסרה מרשימת הסרה
            </AlertDialogHeader>
            <AlertDialogBody>
              {pendingRemove && (
                <>
                  לאחר ההסרה, המספר <b>{pendingRemove.phoneNumber}</b> יחזור לקבל הודעות שיווק.
                  האם להמשיך?
                </>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={cancelRemove}>
                ביטול
              </Button>
              <Button colorScheme="red" onClick={doRemove} ml={3} isLoading={removing}>
                הסר
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default WhatsappOptOutsModal;
